import { useContext, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Paper,
  Stack,
} from '@mui/material'
import API from '../../../../services/API'
import CompanyStep from './CompanyStep'
import LocationStep from './LocationStep'
import TeamStep from './TeamStep'
import FinishStep from './FinishStep'
import { GlobalContext, UserContext } from '../../../../App'

const steps = ['Company Info', 'Base Location', 'Teams & Roles', 'Finish']

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i

const TenantOnboarding = () => {
  const [activeStep, setActiveStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [companySettings, setCompanySettings] = useState({
    address: 'GB',
  })
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const [clinicalRole, setClinicalRole] = useState(
    user?.employee?.clinicalRole ?? '',
  )
  const locationStepRef = useRef(null)

  // Signup creates the owner's employee record without a clinical role, so
  // the Teams & Roles step asks for it. A choice only counts while it is
  // still in the roles list being edited above it.
  const ownClinicalRole = (companySettings.roles ?? []).includes(clinicalRole)
    ? clinicalRole
    : ''

  useEffect(() => {
    API.get('/settings').then(
      (res) => res.status === 200 && setCompanySettings(res.data),
    )
  }, [])

  const validateCompanyStep = () => {
    if (!companySettings.orgName || !companySettings.orgShortName) {
      setToast({
        message: 'Please enter your company name and short name',
        severity: 'error',
      })
      return false
    }
    if (
      !companySettings.replyToEmail ||
      !emailRegex.test(companySettings.replyToEmail)
    ) {
      setToast({
        message: 'Please enter a valid reply-to email address',
        severity: 'error',
      })
      return false
    }
    return true
  }

  // The server checks the role against the saved roles list, so the list is
  // saved first.
  const saveTeamStep = async () => {
    const settingsRes = await API.put('/site-config', {
      roles: companySettings.roles,
      additionalTeams: companySettings.additionalTeams,
    }).catch(() => null)
    if (settingsRes?.status !== 200) return false

    const roleRes = await API.put('/onboarding/my-clinical-role', {
      clinicalRole: ownClinicalRole,
    }).catch(() => null)
    return roleRes?.status === 200
  }

  const handleNext = async () => {
    if (activeStep === 0 && !validateCompanyStep()) return

    if (activeStep === 1) {
      setSaving(true)
      const saved = await locationStepRef.current?.save()
      setSaving(false)
      if (!saved) {
        setToast({
          message:
            'Could not save your base locations - check each base has a unique name',
          severity: 'error',
        })
        return
      }
    }

    if (activeStep === 2) {
      if (!ownClinicalRole) {
        setToast({
          message: 'Please choose your own clinical role',
          severity: 'error',
        })
        return
      }
      setSaving(true)
      const saved = await saveTeamStep()
      setSaving(false)
      if (!saved) {
        setToast({
          message: 'Could not save your clinical role - please try again',
          severity: 'error',
        })
        return
      }
    }

    setActiveStep((prev) => prev + 1)
  }

  const handleBack = () => setActiveStep((prev) => prev - 1)

  const handleFinish = async () => {
    setSaving(true)
    const settings = { ...companySettings }
    delete settings.photograph
    const res = await API.put('/site-config', {
      ...settings,
      setupComplete: true,
    }).catch(() => null)
    setSaving(false)
    if (res?.status === 200) {
      // Land on the Getting Started checklist once the reload picks up
      // setupComplete and MainFrame drops out of setup mode.
      window.location.href = '/getting-started'
    } else {
      setToast({
        message: 'Something went wrong saving your settings',
        severity: 'error',
      })
    }
  }

  const isLastStep = activeStep === steps.length - 1

  return (
    <Box
      className="scrollable"
      sx={{ maxHeight: '100vh', overflowY: 'auto', width: '100%' }}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto', my: 6, px: 2 }}>
        <Stack mb={4}>
          <Typography variant="h4" fontWeight={600}>
            Welcome to AmbuNet 👋
          </Typography>
          <Typography variant="caption">
            Let's get your Organisation Set Up
          </Typography>
        </Stack>

        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper elevation={3} sx={{ mt: 4, p: 4 }}>
          {activeStep === 0 ? (
            <CompanyStep
              companySettings={companySettings}
              setCompanySettings={setCompanySettings}
            />
          ) : activeStep === 1 ? (
            <LocationStep ref={locationStepRef} />
          ) : activeStep === 2 ? (
            <TeamStep
              companySettings={companySettings}
              setCompanySettings={setCompanySettings}
              clinicalRole={ownClinicalRole}
              setClinicalRole={setClinicalRole}
            />
          ) : (
            <FinishStep companySettings={companySettings} />
          )}

          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button disabled={activeStep === 0 || saving} onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              color={isLastStep ? 'success' : 'primary'}
              disabled={saving}
              onClick={isLastStep ? handleFinish : handleNext}
            >
              {isLastStep ? 'Finish Setup' : 'Next'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}

export default TenantOnboarding
